import React, { useState, useEffect } from 'react';
import { Search, User, CheckCircle, XCircle, AlertTriangle, Plus, Info, Eye, Users } from 'lucide-react';
import { fetchWithAuth } from '../../../../utils/api';
import { ROSTER_API } from '../../../../utils/api';
import FormCard from '../../../../components/ui/FormCard';

import '../RegistrationFlow.css';

// Define daily price for observers
const DAILY_PRICE = 30;

// Format currency helper
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const ObserversStep = ({
  event,
  registrationData,
  updateRegistrationData,
  onNext,
  onBack,
  isDarkMode,
  renderNavigation
}) => {
  // State for observers
  const [observers, setObservers] = useState(() => {
    // Make sure observers is always an array
    if (Array.isArray(registrationData.observers)) {
      return registrationData.observers;
    } else if (typeof registrationData.observers === 'object' && registrationData.observers !== null) {
      return Object.values(registrationData.observers);
    } else {
      return [];
    }
  });
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewObserverModal, setShowNewObserverModal] = useState(false);
  const [newObserver, setNewObserver] = useState({
    firstName: '',
    lastName: ''
  });
  const [selectedObservers, setSelectedObservers] = useState(() => {
    // Make sure selectedObservers is always an array
    if (Array.isArray(registrationData.observers)) {
      return registrationData.observers;
    } else if (typeof registrationData.observers === 'object' && registrationData.observers !== null) {
      return Object.values(registrationData.observers);
    } else {
      return [];
    }
  });

  // Fetch roster data
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(ROSTER_API);
        
        // Normalize roster data to ensure firstName and lastName are strings
        const normalizedRoster = (response.roster || []).map(member => ({
          ...member,
          firstName: ensureString(member.firstName),
          lastName: ensureString(member.lastName)
        }));
        
        setRoster(normalizedRoster);
      } catch (err) {
        console.error('Error fetching roster:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRoster();
  }, []);

  // Helper function to ensure a value is a string
  const ensureString = (value) => {
    if (value === null || value === undefined) {
      return '';
    }
    
    if (typeof value === 'string') {
      return value;
    }
    
    if (typeof value === 'object') {
      // If it's an object with an S property (as per error message)
      if (value.S && typeof value.S === 'string') {
        return value.S;
      }
      
      // For other objects, try to get a meaningful string representation
      try {
        const strValue = JSON.stringify(value);
        return strValue !== '[object Object]' ? strValue : '';
      } catch (e) {
        return '';
      }
    }
    
    // For other types (number, boolean, etc.)
    return String(value);
  };

  // Add new observer
  const handleAddNewObserver = () => {
    if (!newObserver.firstName || !newObserver.lastName) {
      alert('Please enter both first and last name');
      return;
    }

    const newObserverEntry = {
      id: Date.now(),
      firstName: ensureString(newObserver.firstName),
      lastName: ensureString(newObserver.lastName),
      isManual: true
    };

    const updatedObservers = [...observers, newObserverEntry];
    setObservers(updatedObservers);
    updateRegistrationData('observers', updatedObservers);
    
    // Reset form
    setNewObserver({
      firstName: '',
      lastName: ''
    });
    setShowNewObserverModal(false);
  };

  // Add roster member as observer
  const handleAddRosterMember = (member) => {
    // Generate a consistent observerId
    const observerId = member.dancerId || `observer-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    
    const newObserverEntry = {
      id: observerId,
      observerId: observerId,
      dancerId: member.dancerId,
      firstName: ensureString(member.firstName),
      lastName: ensureString(member.lastName),
      email: ensureString(member.email) || '',
      fromRoster: true,
      options: {
        days: 1
      }
    };

    // Be sure we have a proper array before adding
    const currentObservers = Array.isArray(observers) ? observers : [];
    const updatedObservers = [...currentObservers, newObserverEntry];
    
    setObservers(updatedObservers);
    
    // Also update selectedObservers for consistency
    const updatedSelectedObservers = Array.isArray(selectedObservers) ? 
      [...selectedObservers, newObserverEntry] : [newObserverEntry];
    
    setSelectedObservers(updatedSelectedObservers);
    
    // Extract observer IDs for backend reference
    const observerIds = updatedObservers.map(o => o.observerId || o.id || o.dancerId);
    
    // Update both observers array (UI) and observerIds (backend)
    updateRegistrationData('observers', updatedObservers);
    updateRegistrationData('observerIds', observerIds);
  };

  // Remove observer
  const handleRemoveObserver = (observerId) => {
    // Filter out the observer from both arrays, checking both id and observerId
    const updatedObservers = observers.filter(o => 
      o.id !== observerId && o.observerId !== observerId
    );
    
    setObservers(updatedObservers);
    
    // Also update selectedObservers
    const updatedSelectedObservers = selectedObservers.filter(o => 
      o.id !== observerId && o.observerId !== observerId
    );
    
    setSelectedObservers(updatedSelectedObservers);
    
    // Extract observer IDs for backend reference
    const observerIds = updatedObservers.map(o => o.observerId || o.id);
    
    // Update both observers array (UI) and observerIds (backend)
    updateRegistrationData('observers', updatedObservers);
    updateRegistrationData('observerIds', observerIds);
  };

  // Check if a person is already added as dancer or teacher
  const isAlreadyRegistered = (dancerId) => {
    // Ensure that we handle all cases where dancers might not be an array
    const dancersArray = Array.isArray(registrationData.dancers) 
      ? registrationData.dancers 
      : (typeof registrationData.dancers === 'object' && registrationData.dancers !== null)
        ? Object.values(registrationData.dancers) 
        : [];
        
    // Now we can safely call .some()
    const isDancer = dancersArray.some?.(d => d.dancerId === dancerId) || false;
    
    const teachersArray = Array.isArray(registrationData.teachers) 
      ? registrationData.teachers 
      : (typeof registrationData.teachers === 'object' && registrationData.teachers !== null)
        ? Object.values(registrationData.teachers) 
        : [];
        
    const isTeacher = teachersArray.some?.(t => t.dancerId === dancerId) || false;
    
    return isDancer || isTeacher;
  };

  // Check if a person is already added as observer
  const isAlreadyObserver = (dancerId) => {
    // Ensure we have a proper array before calling .some()
    const observersArray = Array.isArray(observers) 
      ? observers 
      : (typeof observers === 'object' && observers !== null)
        ? Object.values(observers) 
        : [];
        
    return observersArray.some?.(o => o.dancerId === dancerId) || false;
  };

  // Filter roster based on search query
  const filteredRoster = roster.filter(member => {
    const fullName = `${ensureString(member.firstName)} ${ensureString(member.lastName)}`.toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  // Get initials for avatar - robust implementation to handle any input type
  const getInitials = (firstName, lastName) => {
    const firstStr = ensureString(firstName);
    const lastStr = ensureString(lastName);
    
    const firstInitial = firstStr.charAt(0) || '';
    const lastInitial = lastStr.charAt(0) || '';
    
    return (firstInitial + lastInitial).toUpperCase();
  };

  // Toggle observer selection
  const toggleObserverSelection = (observer) => {
    // Ensure observer has a unique identifier - use observerId, id, or generate one
    const observerId = observer.observerId || observer.id || `observer-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    
    // Ensure we're working with the correct identifier
    const updatedObserver = {
      ...observer,
      observerId: observerId
    };
    
    // Check if already selected, using a safe method that works regardless of structure
    const isSelected = selectedObservers.some?.(o => (o.observerId === observerId || o.id === observerId)) || false;
    
    let updatedObservers;
    
    if (isSelected) {
      // Remove by filtering to ensure we catch it whether it uses observerId or id
      updatedObservers = selectedObservers.filter(o => (o.observerId !== observerId && o.id !== observerId));
    } else {
      // Add with the guaranteed identifier
      updatedObservers = [
        ...selectedObservers, 
        {
          ...updatedObserver,
          options: {
            days: 1
          }
        }
      ];
    }
    
    // Update local state
    setSelectedObservers(updatedObservers);
    
    // Extract observer IDs for backend reference, ensuring we get a unique identifier for each
    const observerIds = updatedObservers.map(o => o.observerId || o.id || `observer-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`);
    
    // Update both observers array (UI) and observerIds (backend)
    updateRegistrationData('observers', updatedObservers);
    updateRegistrationData('observerIds', observerIds);
  };

  // Update observer options
  const updateObserverOptions = (observerId, options) => {
    const updatedObservers = selectedObservers.map(observer => {
      // Match by either observerId or id
      if (observer.observerId === observerId || observer.id === observerId) {
        return {
          ...observer,
          observerId: observer.observerId || observerId, // Ensure observer has observerId
          options: {
            ...observer.options,
            ...options
          }
        };
      }
      return observer;
    });
    
    // Update local state
    setSelectedObservers(updatedObservers);
    
    // Extract observer IDs for backend reference, using a safe approach
    const extractedIds = updatedObservers.map(o => o.observerId || o.id || `observer-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`);
    
    // Update both observers array (UI) and observerIds (backend)
    updateRegistrationData('observers', updatedObservers);
    updateRegistrationData('observerIds', extractedIds);
  };

  // Next button handler
  const handleNext = () => {
    // Calculate final prices for observers
    const observersWithPricing = selectedObservers.map(observer => {
      // Calculate price based on number of days
      const days = observer.options?.days || 1;
      const price = days * DAILY_PRICE;
      
      // Ensure each observer has an observerId and all required fields
      const observerId = observer.observerId || observer.id || `observer-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
      
      return {
        ...observer,
        observerId: observerId, // Ensure consistent observerId field
        firstName: ensureString(observer.firstName),
        lastName: ensureString(observer.lastName),
        calculatedPrice: price
      };
    });
    
    // Extract observer IDs for backend reference using consistent field
    const observerIds = observersWithPricing.map(observer => observer.observerId);
    
    // Update both observers array (UI) and observerIds (backend)
    updateRegistrationData('observers', observersWithPricing);
    updateRegistrationData('observerIds', observerIds);
    
    // Proceed to next step
    onNext(observersWithPricing);
  };

  // Handle new observer form submission
  const handleNewObserverSubmit = (e) => {
    e.preventDefault();
    
    if (!newObserver.firstName || !newObserver.lastName) {
      setError('Please enter a name for the observer');
      return;
    }
    
    // Parse first and last name
    const nameParts = newObserver.firstName.split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Generate a unique observerId
    const observerId = `observer-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
    
    // Create new observer
    const newObserverEntry = {
      id: observerId,
      observerId: observerId, // Add consistent observerId
      firstName,
      lastName,
      isManual: true,
      options: {
        days: 1
      }
    };
    
    // Add to selected observers
    const updatedObservers = [...selectedObservers, newObserverEntry];
    setSelectedObservers(updatedObservers);
    
    // Extract observer IDs for backend reference
    const observerIds = updatedObservers.map(o => o.observerId || o.id);
    
    // Update both observers array (UI) and observerIds (backend)
    updateRegistrationData('observers', updatedObservers);
    updateRegistrationData('observerIds', observerIds);
    
    // Reset form
    setNewObserver({
      firstName: '',
      lastName: ''
    });
    setShowNewObserverModal(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`p-4 rounded-lg ${
        isDarkMode 
          ? 'bg-red-500/10 border border-red-500/20 text-red-400' 
          : 'alert alert-error shadow-lg'
      }`}>
        <AlertTriangle className="h-6 w-6 mr-2" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full">
      {/* Fixed Header */}
      <div className={`p-4 md:p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex-shrink-0">
          <h2 className={`responsive-heading font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <Eye className="h-6 w-6 mr-2 text-blue-500" />
            Add Observers
          </h2>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Register parent observers and guests for {event?.name || ''}
          </p>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="p-4 md:p-6">
        <div className="space-y-8">
          {/* Search Box */}
          <div className={`relative ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            <input
              type="text"
              className={`form-input pr-10 pl-4 py-2 w-full rounded-lg border ${
                isDarkMode 
                  ? 'bg-gray-800 border-gray-700 text-gray-200 focus:border-blue-500 focus:ring-blue-500' 
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500 focus:ring-blue-500'
              } focus:ring-1 focus:outline-none transition-colors duration-200`}
              placeholder="Search roster..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <Search className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            </div>
          </div>
          
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Roster Members */}
            <FormCard 
              title="Available Roster Members" 
              subtitle={`${filteredRoster.length} people found`}
              icon={<Users className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />}
              isDarkMode={isDarkMode}
            >
              <div className="overflow-y-auto max-h-[400px] custom-scrollbar">
                {/* Add New Observer Button */}
                <div 
                  className={`p-3 mb-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                    isDarkMode 
                      ? 'border-blue-800/50 bg-blue-900/10 hover:bg-blue-900/20' 
                      : 'border-blue-200 bg-blue-50 hover:bg-blue-100'
                  }`}
                  onClick={() => setShowNewObserverModal(true)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                        isDarkMode ? 'bg-blue-800 text-blue-300' : 'bg-blue-200 text-blue-700'
                      }`}>
                        <Plus className="h-5 w-5" />
                      </div>
                      <div>
                        <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Add New Observer
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          Not in your roster? Add them here
                        </p>
                      </div>
                    </div>
                    <Plus className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                </div>

                {filteredRoster.length === 0 ? (
                  <div className="text-center py-6">
                    <User className={`h-12 w-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      No roster members found
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Try adjusting your search
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredRoster.map(member => {
                      const isRegistered = isAlreadyRegistered(member.dancerId);
                      const isObserver = isAlreadyObserver(member.dancerId);
                      
                      return (
                        <div 
                          key={member.dancerId || `member-${member.firstName}-${member.lastName}`}
                          className={`p-3 rounded-lg border transition-all duration-200 ${
                            isRegistered || isObserver
                              ? isDarkMode 
                                ? 'border-gray-700 bg-gray-800/50 opacity-50' 
                                : 'border-gray-200 bg-gray-100 opacity-50'
                              : isDarkMode 
                                ? 'border-gray-700 hover:border-gray-600' 
                                : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                                isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {getInitials(member.firstName, member.lastName)}
                              </div>
                              <div>
                                <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {ensureString(member.firstName)} {ensureString(member.lastName)}
                                </h4>
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {isRegistered 
                                    ? 'Already registered as dancer/teacher' 
                                    : isObserver 
                                      ? 'Already added as observer'
                                      : ensureString(member.role) || 'Roster Member'}
                                </p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleAddRosterMember(member)}
                              disabled={isRegistered || isObserver}
                              className={`p-2 rounded-full transition-colors ${
                                isRegistered || isObserver
                                  ? isDarkMode
                                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  : isDarkMode
                                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                              }`}
                            >
                              <Plus className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </FormCard>
            
            {/* Registered Observers */}
            <FormCard 
              title="Registered Observers" 
              subtitle={`${selectedObservers.length} ${selectedObservers.length === 1 ? 'observer' : 'observers'} registered`}
              icon={<Eye className={`h-5 w-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />}
              isDarkMode={isDarkMode}
            >
              <div className="overflow-y-auto max-h-[400px] custom-scrollbar">
                {selectedObservers.length === 0 ? (
                  <div className="text-center py-6">
                    <Eye className={`h-12 w-12 mx-auto mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                    <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      No observers registered yet
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Add observers from your roster or create new observers
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedObservers.map(observer => (
                      <div 
                        key={observer.id || `observer-${Date.now()}-${Math.random()}`}
                        className={`p-4 rounded-lg ${
                          isDarkMode 
                            ? 'bg-gray-800/50 border border-white/5' 
                            : 'bg-base-200'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                              observer.isManual
                                ? isDarkMode 
                                  ? 'bg-purple-900/50 text-purple-300' 
                                  : 'bg-purple-100 text-purple-700'
                                : isDarkMode 
                                  ? 'bg-gray-700 text-gray-300' 
                                  : 'bg-gray-300 text-gray-700'
                            }`}>
                              {getInitials(observer.firstName, observer.lastName)}
                            </div>
                            <div>
                              <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {ensureString(observer.firstName)} {ensureString(observer.lastName)}
                              </h4>
                              <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                                observer.isManual
                                  ? isDarkMode
                                    ? 'bg-purple-500/20 text-purple-400'
                                    : 'bg-purple-100 text-purple-800'
                                  : isDarkMode
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-blue-100 text-blue-800'
                              }`}>
                                {observer.isManual ? 'Added Manually' : 'From Roster'}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveObserver(observer.id)}
                            className={`p-1 rounded-lg transition-colors ${
                              isDarkMode
                                ? 'hover:bg-red-500/10 text-red-400'
                                : 'hover:bg-base-300 text-error'
                            }`}
                          >
                            <XCircle className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </FormCard>
          </div>
          
          {/* New Observer Modal */}
          {showNewObserverModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className={`w-full max-w-md p-6 rounded-lg ${
                isDarkMode 
                  ? 'bg-gray-800 border border-gray-700' 
                  : 'bg-white shadow-xl'
              }`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Add New Observer
                  </h3>
                  <button
                    onClick={() => setShowNewObserverModal(false)}
                    className={`p-1 rounded-full ${
                      isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={newObserver.firstName}
                      onChange={(e) => setNewObserver(prev => ({ ...prev, firstName: e.target.value }))}
                      className={`w-full rounded-lg ${
                        isDarkMode 
                          ? 'bg-gray-700 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
                          : 'input input-bordered'
                      }`}
                      placeholder="Enter first name"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={newObserver.lastName}
                      onChange={(e) => setNewObserver(prev => ({ ...prev, lastName: e.target.value }))}
                      className={`w-full rounded-lg ${
                        isDarkMode 
                          ? 'bg-gray-700 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
                          : 'input input-bordered'
                      }`}
                      placeholder="Enter last name"
                    />
                  </div>
                  
                  <div className="flex justify-end pt-4">
                    <button
                      onClick={() => setShowNewObserverModal(false)}
                      className={`px-4 py-2 rounded-lg mr-2 ${
                        isDarkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'btn btn-ghost'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleNewObserverSubmit}
                      className={`px-4 py-2 rounded-lg ${
                        isDarkMode
                          ? 'bg-blue-600 text-white hover:bg-blue-700'
                          : 'btn btn-primary'
                      }`}
                    >
                      Add Observer
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Action Buttons */}

        </div>
        
        {/* Render standardized navigation */}
        {renderNavigation && renderNavigation(handleNext, onBack, selectedObservers.length > 0 && selectedObservers.every(observer => observer.eventLevel), "Continue")}
      </div>
    </div>
  );
};

export default ObserversStep;